import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldCheck, CreditCard } from 'lucide-react';
import { publicPayAPI, startPublicPayment } from '../../utils/publicPayment';
import DtdAfter8pmNotice from '../../components/common/DtdAfter8pmNotice';
import StbOnNotice from '../../components/common/StbOnNotice';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const outer = {
  minHeight: '100vh', background: '#f1f5f9', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px',
};
const card = {
  width: '100%', maxWidth: 380, background: '#fff', borderRadius: 14,
  boxShadow: '0 10px 40px rgba(0,0,0,0.08)', padding: 28, textAlign: 'center',
};

// Public, no-login "pay your bill" page — reached via a link generated on the Customer's
// paymentLinkToken (see backend utils/paymentLink.js), shared through WhatsApp reminders/
// bill messages/Advance Payment Link. Pays off the account's CURRENT outstanding balance,
// unless the link carries ?amt= — an advance link, where the agent is collecting for next
// month and the live due isn't the figure to charge. Either way it's purpose:'balance'
// server-side, so an advance just leaves the account in credit; no plan-duration picker.
export default function PayBillPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const askedAmount = Number(searchParams.get('amt')) || null;
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [show8pmNotice, setShow8pmNotice] = useState(false);
  const [showStbNotice, setShowStbNotice] = useState(false);

  useEffect(() => {
    publicPayAPI.getSummary(token, askedAmount)
      .then(({ data }) => setSummary(data.data))
      .catch((err) => setError(err.response?.data?.message || 'Payment link not found'))
      .finally(() => setLoading(false));
  }, [token, askedAmount]);

  // Server-computed: a DTD CableTV bill paid after 8 PM counts from tomorrow — see
  // backend routes/publicPay.js.
  // stbOnNotice is also server-computed (utils/stbOnNotice.js explains the rule); it comes first.
  const afterStbNotice = () => {
    setShowStbNotice(false);
    if (summary?.dtdStartsTomorrow) setShow8pmNotice(true);
    else handlePay();
  };
  const onPayClick = () => (summary?.stbOnNotice ? setShowStbNotice(true) : afterStbNotice());

  const handlePay = async () => {
    setShow8pmNotice(false);
    setPaying(true);
    try {
      await startPublicPayment({
        token,
        amount: summary?.requestedAmount || undefined,
        merchantName: summary?.merchantName,
        onSuccess: ({ receiptNumber }) => {
          navigate(`/pay/${token}/result?payment=success${receiptNumber ? `&receipt=${receiptNumber}` : ''}`);
        },
        onFailure: (message) => {
          if (message) toast.error(message);
          setPaying(false);
        },
      });
      // AblePay's branch navigates the browser away entirely — paying stays true until
      // then, which is fine, the page is about to unload.
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start payment — please try again.');
      setPaying(false);
    }
  };

  if (loading) {
    return <div style={outer}><div className="spinner" style={{ marginTop: 80 }} /></div>;
  }

  if (error) {
    return (
      <div style={outer}>
        <div style={card}>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>Link not found</h2>
          <p style={{ color: '#6b7280', fontSize: '.9rem' }}>{error}</p>
        </div>
      </div>
    );
  }

  const balance = summary?.outstandingBalance || 0;
  // The server is the authority on whether the ?amt= hint survived validation — trusting
  // the raw query param here would let the page advertise an amount the order won't use.
  const asked = summary?.requestedAmount || null;
  // Server-computed max(asked, live balance) — so a link generated before a monthly
  // auto-bill (or a cash payment recorded elsewhere) changed the balance always displays
  // what's genuinely owed today, never the stale figure baked into an old link.
  const payable = summary?.payableAmount ?? (asked || balance);

  return (
    <div style={outer}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', background: '#1e293b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CreditCard size={24} color="#fff" />
          </div>
        </div>
        <div style={{ fontSize: '.82rem', color: '#6b7280' }}>{summary.name} needs to pay</div>
        <div style={{ fontSize: '.78rem', color: '#9ca3af', marginBottom: 16 }}>Merchant: {summary.merchantName}</div>

        <div style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', marginBottom: asked && payable > balance ? 6 : 20 }}>
          ₹{fmt(payable)}
        </div>
        {asked && payable > balance && (
          <div style={{ fontSize: '.75rem', color: '#9ca3af', marginBottom: 20 }}>
            {balance > 0 ? `Current balance due: ₹${fmt(balance)}` : 'Advance payment — your account is fully paid up'}
          </div>
        )}

        {payable <= 0 ? (
          <p style={{ color: '#6b7280', fontSize: '.9rem' }}>No outstanding balance — you're all paid up.</p>
        ) : !summary.payEnabled ? (
          <p style={{ color: '#6b7280', fontSize: '.9rem' }}>
            Online payment isn't set up for this account yet — please contact us to pay this bill.
          </p>
        ) : (
          <>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px 0', fontSize: '1rem' }}
              onClick={onPayClick}
              disabled={paying}
            >
              {paying ? 'Redirecting…' : 'Pay Now'}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 14, color: '#9ca3af', fontSize: '.75rem' }}>
              <ShieldCheck size={14} /> Secure payment — UPI, card &amp; net banking
            </div>
            {showStbNotice && <StbOnNotice onConfirm={afterStbNotice} onCancel={() => setShowStbNotice(false)} />}
            {show8pmNotice && <DtdAfter8pmNotice onConfirm={handlePay} onCancel={() => setShow8pmNotice(false)} />}
          </>
        )}
      </div>
    </div>
  );
}
